import { StackScreenProps } from "@react-navigation/stack";
import { ManageSmartPayRootParamList } from "../../../../types";
import {
  PairSmartPayForm,
  PairSmartPayFormValues,
} from "../../components/PairSmartPayForm";
import { ScreenContext } from "./context";
import React from "react";
import {
  Button,
  Card,
  ControlledTextField,
  FlatActionList,
  Page,
  Stack,
  Text,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Colors, View } from "react-native-ui-lib";
import { useActiveTerminal } from "../../../../utilities/app-state/hooks";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import {
  generateDeviceId,
  POS_VENDOR_NAME,
} from "../../../../utilities/smartpay";
import { sendPairingRequest } from "../../../../utilities/smartpay/sdk";

export interface SmartPayPairingScreenProps
  extends StackScreenProps<
    ManageSmartPayRootParamList,
    "SmartPayPairingScreen"
  > {}

const Content = ({ navigation }: SmartPayPairingScreenProps) => {
  const flashMessagesContext = useFlashMessages();

  const context = React.useContext(ScreenContext);
  const { control, formState, reset, watch, handleSubmit } =
    useFormContext<PairSmartPayFormValues>();

  const { t } = useTranslation("settings");

  const watchedPosTerminalName = watch("posRegisterName");
  const watchedPosBusinessName = watch("posBusinessName");
  const watchedPosVendorName = watch("posVendorName");

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  React.useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  if (!context) {
    return null; // or a loading indicator
  }

  const handleOnSubmit = async (data: PairSmartPayFormValues) => {
    try {
      const posRegisterId = generateDeviceId(
        data.terminalId,
        data.paymentMethodId
      );

      const result = await sendPairingRequest(data.pairCode, {
        input: {
          POSBusinessName: data.posBusinessName,
          POSVendorName: data.posVendorName,
          POSRegisterName: data.posRegisterName,
          POSRegisterID: posRegisterId,
        },
      });
      if ("error" in result) {
        flashMessagesContext.showMessage({
          severity: "error",
          message: result.error,
        });
        return;
      } else if ("result" in result) {
        flashMessagesContext.showMessage({
          severity: "success",
          message: t("message.smartpay_pairing_success"),
        });
        navigation.goBack();
      } else {
        throw new Error("Unexpected response from SmartPay pairing request.");
      }
    } catch (error) {
      flashMessagesContext.showMessage({
        severity: "error",
        message: t("message.smartpay_pairing_error"),
      });
    }
  };

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const title = `Pair SmartPay Terminal - ${context.paymentMethod.name}`;
  const secondaryTitle =
    "Enter the pairing code from your SmartPay terminal to connect it with this payment method. If any details change, you will be required to re-pair the terminal.";

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_smartpay_pairing")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page title={title} secondaryTitle={secondaryTitle} footer={footerMarkup}>
      <Stack vertical>
        <Card title="Pairing information">
          <FlatActionList
            items={[
              {
                key: "vendor_name",
                title: "Vendor Name",
                subTitle: "Name of POS App (Roam).",
                componentRight: () => {
                  return (
                    <View>
                      <Text>{watchedPosVendorName}</Text>
                    </View>
                  );
                },
              },
              {
                key: "business_name",
                title: "Business Name",
                subTitle:
                  "Name of the business that will be displayed on the terminal.",
                componentRight: () => {
                  return (
                    <View>
                      <Text>{watchedPosBusinessName}</Text>
                    </View>
                  );
                },
              },
              {
                key: "register_name",
                title: "Register Name",
                subTitle:
                  "Name of the register that will be displayed on the terminal.",
                componentRight: () => {
                  return (
                    <View>
                      <Text>{watchedPosTerminalName}</Text>
                    </View>
                  );
                },
              },
            ]}
          />
        </Card>
        <Card title="Pair SmartPay Terminal">
          <ControlledTextField
            control={control}
            label="Please enter the 8 digit pairing code shown on your terminal display."
            placeholder="Pair code"
            name="pairCode"
            filled={false}
          />
        </Card>
      </Stack>
    </Page>
  );
};

export const SmartPayPairingScreen = (props: SmartPayPairingScreenProps) => {
  const globalSettings = useGlobalSettingsProvider();
  const { terminal } = useActiveTerminal();
  const screenContext = React.useContext(ScreenContext);

  if (!terminal || !globalSettings || !screenContext) {
    return null; // or a loading indicator
  }

  return (
    <PairSmartPayForm
      defaultFormValues={{
        posVendorName: POS_VENDOR_NAME,
        posBusinessName: globalSettings.businessName || "",
        posRegisterName: terminal.name || "",
        pairCode: "",
        terminalId: terminal.id,
        paymentMethodId: screenContext.paymentMethod.id,
      }}
    >
      <Content {...props} />
    </PairSmartPayForm>
  );
};
